import get from 'lodash/get';

import { InvoiceDiscrepancy, InvoiceLineItemDiscrepancy } from 'services/invoiceDiscrepancies/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatCurrencyOrEmptyValue } from 'support/helpers/currency/currency';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { InvoiceVerificationDTO, MessageV2DTO, TransactionsRoutes } from 'support/types';

/**
 * The dispute email is written in the language the user picked in the modal, which is independent of
 * the language the Portal itself is rendered in. Callers therefore pass a `t` bound to that language
 * instead of this module reaching for the global one.
 */
export type TranslateEmail = (key: string, options?: Record<string, unknown>) => string;

type GenerateEmailBodyArgs = {
  invoice: MessageV2DTO;
  invoiceVerification: InvoiceVerificationDTO;
  discrepancies: Array<InvoiceDiscrepancy>;
  t: TranslateEmail;
};
export const generateEmailBody = ({ invoice, invoiceVerification, discrepancies, t }: GenerateEmailBodyArgs) => {
  const discrepanciesByLineItems = organizeDiscrepanciesByLineItem(discrepancies);
  return `
    <p>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.greeting', { partner: invoice.sender_partner?.name })}</br>
    ${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.intro', {
      invoiceNumber: `<a href="${getInvoiceUrl(invoice)}">${invoice.external_message_ref}</a>`,
    })}</p></br>
    <ul>
      ${Object.keys(discrepanciesByLineItems)
        .map((lineItemPath) => {
          const lineItemDiscrepancies = discrepanciesByLineItems[lineItemPath];
          const lineItem = get(invoiceVerification.payload, lineItemPath) as
            | Required<Required<InvoiceVerificationDTO>['payload']>['matched_line_items'][number]
            | undefined;

          return `<li>
            ${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.lineItemLabel', { name: lineItem?.item?.name, gtin: lineItem?.item?.identifiers.gtin ?? EMPTY_FIELD, supplierArticleNumber: lineItem?.item?.identifiers.supplier ?? EMPTY_FIELD })}</br>
            ${getLineItemDiscrepancies(lineItemDiscrepancies, t).join('</br>')}
          </li>`;
        })
        .join('</br>')}
    </ul>
    ${getChargeDiscrepancies(discrepancies, t)}
    <p></br>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.closingRequest')}</p>
    <p>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.thankYou')}</br>
    ${invoice.receiver_partner?.name}</p>
  `;
};

const getInvoiceUrl = (invoice: MessageV2DTO) => {
  return `${window.location.origin}${routeToPage(TransactionsRoutes.viewMessage, { id: invoice.id })}`;
};

const getChargeDiscrepancies = (discrepancies: Array<InvoiceDiscrepancy>, t: TranslateEmail) => {
  const chargeDiscrepancies = discrepancies
    .filter((discrepancy) => discrepancy.type === 'charges')
    .map((discrepancy) => {
      const { data, currency } = discrepancy;
      return `
        <li>
          ${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.chargeDiscrepancy.item', {
            description: data.description,
            amount: formatCurrencyOrEmptyValue(data.actual?.amount, currency),
          })}
        </li>`;
    });

  if (chargeDiscrepancies?.length > 0) {
    return `</br>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.chargeDiscrepancy.label', { count: chargeDiscrepancies?.length })}<ul>${chargeDiscrepancies.join('')}</ul>`;
  }

  return '';
};

const organizeDiscrepanciesByLineItem = (
  discrepancies: Array<InvoiceDiscrepancy>,
): Record<string, Array<InvoiceLineItemDiscrepancy>> => {
  return discrepancies.reduce(
    (acc, discrepancy) => {
      if (discrepancy.type === 'charges' || discrepancy.type === 'allowances') {
        return acc; // Skip charge discrepancies for line item grouping
      }
      const lineItemPath = discrepancy.lineItemPath;
      if (!acc[lineItemPath]) {
        acc[lineItemPath] = [];
      }
      acc[lineItemPath].push(discrepancy);
      return acc;
    },
    {} as Record<string, Array<InvoiceLineItemDiscrepancy>>,
  );
};

const getLineItemDiscrepancies = (
  discrepancies: Array<InvoiceLineItemDiscrepancy>,
  t: TranslateEmail,
): Array<string> => {
  return discrepancies
    .map((discrepancy) => {
      switch (discrepancy.type) {
        case 'prices':
          return `<span>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.priceDiscrepancy', {
            expected: formatCurrencyOrEmptyValue(
              discrepancy.data.consolidated_unit_price.expected,
              discrepancy.currency,
            ),
            actual: formatCurrencyOrEmptyValue(discrepancy.data.consolidated_unit_price?.actual, discrepancy.currency),
          })}</span>`;
        case 'quantities':
          return `<span>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.quantityDiscrepancy', {
            ordered: discrepancy.data.ordered_quantity.value,
            accepted: discrepancy.data.accepted_quantity.value,
            invoiced: discrepancy.data.invoiced_quantity.value,
          })}</span>`;
        case 'taxes':
          return `<span>${t('invoiceVerification:detailsPage.generateDiscrepancyEmail.email.taxDiscrepancy', {
            expected: discrepancy.data.expected?.percentage,
            actual: discrepancy.data.actual?.percentage,
          })}</span>`;
        default:
          return '';
      }
    })
    .filter(Boolean);
};
